import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useTranslation } from "react-i18next";

import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";

import { useMutation } from "@apollo/client";
import { MUTATION_FORGOT_PASSWORD } from "../../config/graphql/mutation";

import { useDomainCheck } from "../../lib/hooks/useDomainCheck";
import DefaultForgotPassword from "./DefaultForgotPassword";
import CenterParcsForgotPassword from "./CenterParcsForgotPassword";

type FieldValues = {
  email: string;
};

const ForgotPassword = React.memo(() => {
  const history = useHistory();
  const { t } = useTranslation("forgotPassword");

  const { isCenterParcsDomain } = useDomainCheck();

  const schema = useMemo(() => {
    return yup.object().shape({
      email: yup
        .string()
        .required(t("forgotPassword:yup.email.required"))
        .email(t("forgotPassword:yup.email.pattern")),
    });
  }, [t]);

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    defaultValues: { email: "" },
  });

  const [onForgotPassword, { loading }] = useMutation(MUTATION_FORGOT_PASSWORD);

  const onSubmit = useCallback(
    (input: FieldValues) => {
      const normalizedInput = {
        ...input,
        email: input.email.trim().toLowerCase(),
      };

      return onForgotPassword({ variables: { input: normalizedInput } })
        .then(() => {
          toast.success<string>(t("forgotPassword:toast.success"));
          history.push("/login");
        })
        .catch(() => {
          toast.error<string>(t("forgotPassword:toast.error"));
          history.push("/login");
        });
    },
    [onForgotPassword, t, history],
  );

  const forgotPasswordProps = {
    onSubmit: methods.handleSubmit(onSubmit),
    loading,
    t,
  };

  if (isCenterParcsDomain) {
    return (
      <FormProvider {...methods}>
        <CenterParcsForgotPassword {...forgotPasswordProps} />
      </FormProvider>
    );
  }

  return (
    <FormProvider {...methods}>
      <DefaultForgotPassword {...forgotPasswordProps} />
    </FormProvider>
  );
});

export default ForgotPassword;
